import React from 'react';
import styled from 'styled-components';
import { format } from 'date-fns-tz';
import { Icon } from 'semantic-ui-react';

import DoughnutGauge from 'areas/dashboard/components/DoughnutGauge';
import { validateCronExpression } from 'common/components/CronExpressionInput';
import StatusLabel from 'common/components/StatusLabel';

import type { PulseParticipation, PulseSurvey } from '../api/pulseSurveyPortalApi';

export type PulseSurveySummaryCardProps = {
  survey: PulseSurvey;
  participation: PulseParticipation;
  /** Participants whose invite email failed. */
  failedInvites: number;
  orgTimezone?: string;
  onToggleReminder?: () => void;
  reminderToggleBusy?: boolean;
  actionsControl?: React.ReactNode;
};

const PULSE_STATUS_SCHEDULED = 1;
const PULSE_STATUS_IN_PROGRESS = 2;
const PULSE_STATUS_CLOSED = 3;

const formatDateParts = (iso?: string | null): { primary: string; secondary: string } => {
  if (!iso) return { primary: '-', secondary: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { primary: String(iso), secondary: '' };
  return {
    primary: format(d, 'MMM. d, yyyy (EEE)'),
    secondary: format(d, 'h:mm a z'),
  };
};

const scheduleTitle = (iso?: string | null): string => {
  if (!iso) return 'Pulse Survey';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Pulse Survey';
  return format(d, 'MMM d, yyyy');
};

export const pulseSurveyStatus = (survey: PulseSurvey, finalized: boolean) => {
  if (finalized) return { label: 'Finalized', color: '#424242' };

  if (survey.status === PULSE_STATUS_SCHEDULED) return { label: 'Scheduled', color: '#9A9A9A' };
  if (survey.status === PULSE_STATUS_IN_PROGRESS) return { label: 'In Progress', color: '#1976d2' };
  if (survey.status === PULSE_STATUS_CLOSED) return { label: 'Closed', color: '#2e7d32' };

  return { label: 'Unknown', color: '#7A7A7A' };
};

/** Same layout as the assessment schedule summary card. */
const PulseSurveySummaryCard: React.FC<PulseSurveySummaryCardProps> = ({
  survey,
  participation,
  failedInvites,
  orgTimezone,
  onToggleReminder,
  reminderToggleBusy = false,
  actionsControl,
}) => {
  const status = pulseSurveyStatus(survey, participation.finalized);
  const responseRate = participation.response_rate ?? 0;
  const completedCount = participation.completed ?? 0;
  const showInvitationsRow = survey.status === PULSE_STATUS_SCHEDULED && !participation.finalized;
  const invitedCount = participation.invited ?? 0;
  const start = formatDateParts(survey.start_date_time);
  const end = formatDateParts(survey.end_date_time);
  const cronInfo = validateCronExpression(survey.reminder_schedule || '', orgTimezone);
  const reminderScheduleText = survey.reminder_schedule
    ? cronInfo.valid
      ? cronInfo.plainEnglish
      : survey.reminder_schedule
    : 'Not set';

  return (
    <SummaryLayout data-testid="pulse-participation-summary">
      <SummaryLeadColumn>
        <SurveyNameCell>
          <DoughnutGauge complete={responseRate * 100} percent={true} size="smallPlus" />
          <SurveyNameText>
            <SurveyTitle>{scheduleTitle(survey.start_date_time)}</SurveyTitle>
            <SurveyMeta>
              {invitedCount} invitation{invitedCount === 1 ? '' : 's'} • {completedCount} response
              {completedCount === 1 ? '' : 's'}
              {failedInvites > 0 && (
                <Icon
                  name="warning sign"
                  color="orange"
                  size="small"
                  title={`${failedInvites} invitation ${failedInvites === 1 ? 'issue' : 'issues'} were not delivered.`}
                />
              )}
            </SurveyMeta>
          </SurveyNameText>
        </SurveyNameCell>
      </SummaryLeadColumn>

      <SummaryTableScroll>
        <SummaryTable>
          <SummaryHeaderRow>
            <SummaryHeaderCell>Start</SummaryHeaderCell>
            <SummaryHeaderCell>End</SummaryHeaderCell>
            <SummaryHeaderCell>Status</SummaryHeaderCell>
            <SummaryHeaderCell $center>Reminders</SummaryHeaderCell>
            <SummaryHeaderCell $center>Actions</SummaryHeaderCell>
          </SummaryHeaderRow>

          <SummaryDataRow>
          <SummaryCell>
            <DatePrimary>{start.primary}</DatePrimary>
            {start.secondary && (
              <>
                <br />
                <DateSecondary>{start.secondary}</DateSecondary>
              </>
            )}
          </SummaryCell>

          <SummaryCell>
            <DatePrimary>{end.primary}</DatePrimary>
            {end.secondary && (
              <>
                <br />
                <DateSecondary>{end.secondary}</DateSecondary>
              </>
            )}
          </SummaryCell>

          <SummaryCell>
            <StatusLabel color={status.color}>{status.label}</StatusLabel>
          </SummaryCell>

          <SummaryCell $center>
            <ReminderControl title={reminderScheduleText}>
              <ReminderToggle
                type="button"
                aria-pressed={Boolean(survey.reminder_enabled)}
                aria-label={`${survey.reminder_enabled ? 'Disable' : 'Enable'} reminders`}
                title={survey.reminder_enabled ? 'Disable reminders' : 'Enable reminders'}
                disabled={reminderToggleBusy}
                onClick={onToggleReminder}
              >
                <Icon
                  name={survey.reminder_enabled ? 'bell' : 'bell slash'}
                  color={survey.reminder_enabled ? 'green' : 'grey'}
                  size="large"
                />
              </ReminderToggle>
              <ReminderState $enabled={Boolean(survey.reminder_enabled)}>
                {survey.reminder_enabled ? 'On' : 'Off'}
              </ReminderState>
            </ReminderControl>
          </SummaryCell>

          <SummaryCell $center>
            {actionsControl}
          </SummaryCell>
          </SummaryDataRow>
        </SummaryTable>
      </SummaryTableScroll>
    </SummaryLayout>
  );
};

export default PulseSurveySummaryCard;

const SummaryLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(240px, 1.25fr) minmax(620px, 2.25fr);
  gap: 10px;
  width: 100%;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const SummaryLeadColumn = styled.div`
  padding-top: 6px;
`;

const SummaryTableScroll = styled.div`
  width: 100%;
  overflow: visible;

  @media (max-width: 1100px) {
    overflow-x: auto;
  }
`;

const SummaryTable = styled.div`
  min-width: 560px;
  width: 100%;
`;

const SummaryHeaderRow = styled.div`
  display: grid;
  grid-template-columns: minmax(150px, 1.2fr) minmax(150px, 1.2fr) minmax(110px, 0.9fr) minmax(120px, 0.9fr) minmax(90px, 0.7fr);
`;

const SummaryDataRow = styled.div`
  display: grid;
  grid-template-columns: minmax(150px, 1.2fr) minmax(150px, 1.2fr) minmax(110px, 0.9fr) minmax(120px, 0.9fr) minmax(90px, 0.7fr);
`;

const SummaryHeaderCell = styled.div<{ $center?: boolean }>`
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #344054;
  padding: 6px 6px 1px;
  text-align: ${({ $center }) => ($center ? 'center' : 'left')};
`;

const SummaryCell = styled.div<{ $center?: boolean }>`
  padding: 0px 6px 10px;
  text-align: ${({ $center }) => ($center ? 'center' : 'left')};
  align-self: center;
`;

const SurveyNameCell = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const SurveyNameText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
`;

const SurveyTitle = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 18px;
  font-weight: 500;
  line-height: 1.25;
`;

const SurveyMeta = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #666;
  margin-top: 2px;
`;

const DatePrimary = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
`;

const DateSecondary = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
`;

const ReminderControl = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;

const ReminderState = styled.span<{ $enabled: boolean }>`
  cursor: help;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${({ $enabled }) => ($enabled ? '#166534' : '#6b7280')};
`;

const ReminderToggle = styled.button`
  border: none;
  background: none;
  padding: 0;
  border-radius: 8px;
  cursor: pointer;
  line-height: 0;

  &:hover:not(:disabled) {
    background: #f0f2f5;
  }

  &:focus-visible {
    outline: 2px solid #2d70e2;
  }

  &:disabled {
    cursor: progress;
    opacity: 0.6;
  }

  i.icon {
    margin: 0;
    vertical-align: middle;
  }
`;
